import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticationDataService } from '@warema/shared/authentication/data-access';
import { CurrentAuthenticationState } from '@warema/shared/authentication/models';
import { User } from '@warema/shared/models';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthenticationApiService } from './authentication-api.service';

@Injectable()
export class AuthenticationApiBrowserService extends AuthenticationApiService {
    private readonly _authenticationData = inject(AuthenticationDataService);

    constructor() {
        super();

        this.language$.next('');

        this.user$.pipe(takeUntilDestroyed()).subscribe(userData => {
            if (userData) {
                this.loggedIn$.next(true);

                this.userLoaded$.next(true);
                this.firstName$.next(userData.firstName);
                this.lastName$.next(userData.lastName);
                this.name$.next(`${userData.firstName} ${userData.lastName}`);
                this.email$.next(userData.email);
                this.id$.next(userData.id);
                this.locale$.next(userData.locale);
            } else {
                this.loggedIn$.next(false);

                this.userLoaded$.next(false);
                this.firstName$.next('');
                this.lastName$.next('');
                this.name$.next('');
                this.email$.next('');
                this.id$.next('');
                this.locale$.next('');
            }
        });

        this._authenticationData.authenticationComplete$
            .pipe(takeUntilDestroyed())
            .subscribe(this.authenticationComplete$);

        this._authenticationData.token$.pipe(takeUntilDestroyed()).subscribe(this.token$);
    }

    public get currentAuthenticationState$(): Observable<CurrentAuthenticationState> {
        return this._authenticationData.currentAuthenticationState$;
    }

    public get user$(): BehaviorSubject<User | null> {
        return this._authenticationData.user$;
    }

    public async init(loadUserCallback?: () => Promise<void>): Promise<void> {
        await this._authenticationData.init(loadUserCallback);
    }

    public async login(redirectUri?: string): Promise<void> {
        await this._authenticationData.login(redirectUri);
    }

    public async register(redirectUri?: string): Promise<void> {
        await this._authenticationData.register(redirectUri);
    }

    public logout(): Promise<void> {
        return this._authenticationData.logout();
    }

    public logoutWithPopup(): void {
        this._authenticationData.logoutWithPopup();
    }

    public loginWithPopup(): Promise<void> {
        return this._authenticationData.loginWithPopupAndPoll();
    }

    public closeLoginPopup(): void {
        this._authenticationData.closeLoginPopup();
    }

    public addRegisteredFlag(): void {
        this._authenticationData.addRegisteredFlag();
    }
}
